import { dxtToLocalServerTime, getMomentTz } from "@dexteel/mesf-core";

import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";
import { isNumber } from "lodash-es";

import {
	BooleanCellRenderer,
	CommentIconCellRenderer,
	LinkCellRenderer,
	NumericCellRenderer,
} from "../../../controls/ag-grid/components/ButtonCellRenderer";
import { CasterProduction } from "../../caster/models/CasterProduction";

interface Props {
	setComment: (comment: string) => void;
	setShowCommentModal: (open: boolean) => void;
	casterProductions: CasterProduction[];
}
const moment = getMomentTz();
export const useProductionTableData = ({
	casterProductions,
	setComment,
	setShowCommentModal,
}: Props) => {
	const columnData: (ColDef | ColGroupDef)[] = [
		{
			headerName: "Heat",
			type: ["centerAligned"],
			children: [
				{
					field: "HeatNameValue",
					headerName: "",
					minWidth: 66,
					type: ["numericColumn", "numericWidth"],
					cellRenderer: (params: any) => {
						return LinkCellRenderer({
							...params,
							link: `/meltshop-heats/${params.data.ShiftId}?heatId=${params.data.HeatId}`,
						});
					},
				},
			],
		},
		{
			headerName: "Grade",
			children: [
				{
					field: "GradeValue",
					headerName: "",
					minWidth: 79,
				},
			],
		},
		{
			headerName: "Billet Size",
			children: [
				{
					field: "BilletSizeName",
					headerName: "",
					minWidth: 84,
				},
			],
		},
		{
			headerName: "Open",
			type: ["centerAligned"],
			children: [
				{
					field: "OpenFreeValue",
					headerName: "Free",
					minWidth: 55,
					cellRenderer: BooleanCellRenderer,
					type: ["nonSorteable", "nonFilterable", "centerAligned"],
					cellStyle: {
						padding: "0px",
						textAlign: "center",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					},
				},
				{
					field: "OpenBurnValue",
					headerName: "Burn",
					minWidth: 55,
					cellRenderer: BooleanCellRenderer,
					type: ["nonSorteable", "nonFilterable", "centerAligned"],
					cellStyle: {
						padding: "0px",
						textAlign: "center",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					},
				},
			],
		},
		{
			headerName: "Nozzle",
			children: [
				{
					field: "NozzleSize",
					headerName: "Billet Size Children",
					minWidth: 76,
					cellStyle: {
						textAlign: "right",
					},
				},
			],
		},
		{
			headerName: "Ladle Trim",
			children: [
				{
					field: "TrimTemperature",
					headerName: "Temp",
					minWidth: 60,
					cellStyle: {
						textAlign: "right",
					},
				},
				{
					field: "TrimSuperHeatValue",
					headerName: "SHeat",
					minWidth: 62,
					cellStyle: {
						textAlign: "right",
					},
				},
				{
					field: "TrimProbeOxygen",
					headerName: "Trim O2",
					minWidth: 57,
					type: ["numericColumn", "numericWidth"],
				},
			],
		},
		{
			headerName: "Tundish",
			children: [
				{
					field: "TundishNumber",
					headerName: "Num",
					minWidth: 72,
					type: ["numericColumn", "numericWidth"],
				},
			],
		},
		{
			headerName: "Temperatures",
			children: [
				{
					field: "TemperaturesLiquidus",
					headerName: "Liquidus",
					minWidth: 76,
					type: ["numericColumn", "numericWidth"],
				},
				{
					field: "TemperaturesFirst",
					headerName: "First",
					minWidth: 60,
					type: ["numericColumn", "numericWidth"],
				},
				{
					field: "TemperaturesLast",
					headerName: "Last",
					minWidth: 60,
					type: ["numericColumn", "numericWidth"],
				},
				{
					field: "TemperaturesMinSHValue",
					headerName: "MinSH",
					minWidth: 66,
					type: ["numericColumn", "numericWidth"],
				},
			],
		},
		{
			headerName: "Times",
			children: [
				{
					field: "LadleOpen",
					headerName: "Open",
					minWidth: 62,
					valueGetter: (params) =>
						params.data.LadleOpen
							? moment(params.data.LadleOpen).format("HH:mm")
							: "",
					cellRenderer: (params: any) => {
						return LinkCellRenderer({
							...params,
							link: `/caster-data-entry/${params.data.ProcessId}`,
						});
					},
				},
				{
					field: "LadleClose",
					headerName: "Close",
					minWidth: 62,
					valueGetter: (params) =>
						params.data.LadleClose
							? moment(params.data.LadleClose).format("HH:mm")
							: "",
				},
				{
					field: "DurationValue",
					headerName: "Duration",
					minWidth: 76,
					valueFormatter: (params) => params.data.DurationValue?.toFixed(1),
				},
			],
		},
		{
			headerName: "Avg Speed",
			children: [
				{
					field: "AvgSpeedS1Value",
					headerName: "S1",
					minWidth: 62,
					cellRenderer: NumericCellRenderer,
					cellRendererParams: { numericFormat: "0.0" },
					cellStyle: {
						textAlign: "right",
					},
				},
				{
					field: "AvgSpeedS2Value",
					headerName: "S2",
					minWidth: 62,
					cellRenderer: NumericCellRenderer,
					cellRendererParams: { numericFormat: "0.0" },
					cellStyle: {
						textAlign: "right",
					},
				},
				{
					field: "AvgSpeedS3Value",
					headerName: "S3",
					minWidth: 62,
					cellRenderer: NumericCellRenderer,
					cellRendererParams: { numericFormat: "0.0" },
					cellStyle: {
						textAlign: "right",
					},
				},
			],
		},
		{
			headerName: "Products",
			children: [
				{
					field: "ProductsPieces",
					headerName: "Pieces",
					minWidth: 64,
					type: ["numericColumn", "numericWidth"],
				},
				{
					field: "ProductsTons",
					headerName: "Tons",
					minWidth: 55,
					type: ["numericColumn", "numericWidth"],
					valueGetter: (params) => {
						if (
							isNumber(params.data.ProductsTons) &&
							params.data.ProductsTons > 0
						)
							return Number.parseFloat(params.data.ProductsTons).toFixed(1);
						else return 0;
					},
				},
			],
		},
		{
			field: "ScrapTons",
			headerName: "Scrap [t]",
			minWidth: 69,
			valueGetter: (params) => {
				if (isNumber(params.data.ScrapTons) && params.data.ScrapTons > 0)
					return Number.parseFloat(params.data.ScrapTons).toFixed(1);
				else return 0;
			},
		},
		{
			headerName: "Comments",
			children: [
				{
					field: "Comments",
					headerName: "",
					minWidth: 89,
					cellRenderer: (params: ICellRendererParams) => {
						return (
							<CommentIconCellRenderer
								comments={params.data.Comments}
								onClick={() => {
									setComment(params.data.Comments);
									setShowCommentModal(true);
								}}
							/>
						);
					},
				},
			],
		},
	];

	const defaultColDef: ColDef = {
		flex: 1,
		resizable: true,
		suppressHeaderMenuButton: true,
		wrapHeaderText: true,
		autoHeaderHeight: true,
		filter: false,
		floatingFilter: false,
		sortable: false,
		editable: false,
	};
	return { columnData, defaultColDef };
};
